/**
 * Persists "last opened dashboard" per user across sessions so Dash
 * lands on the most recently used dashboard instead of the empty
 * landing page.
 *
 * Storage: `dashboard_preferences.last_opened_id` on the user-settings
 * doc (Cosmos `settings` container, owner_id=user, type="user").
 *
 * The setter is fire-and-forget — navigation never blocks on the
 * Cosmos write.
 */

import { useCallback } from "react"

import type { DashboardPreferences } from "@/types/user-settings"

import { useUpdateSettingsSection, useUserSettings } from "@/hooks/use-user-settings"

export interface DashboardLastOpened {
  /**
   * The user's stored last-opened dashboard id, or null when there is
   * none yet. Returns `undefined` while user-settings are still
   * loading — callers should treat that as "not yet known".
   */
  lastOpenedId: string | null | undefined
  /** True until the user-settings query has resolved at least once. */
  isLoading: boolean
  /** Persist a new last-opened id (or null to clear). Fire-and-forget. */
  setLastOpenedId: (id: string | null) => void
}

/**
 * Pure helper extracted for unit testing. Resolves the persisted last-
 * opened id and produces a setter payload merged onto the existing
 * preferences. Returns `null` from `buildPayload` when the call should
 * be skipped (no change).
 */

export function _computeLastOpened(
  prefs: DashboardPreferences | undefined,
  isLoading: boolean
): { lastOpenedId: string | null | undefined } {
  if (isLoading) return { lastOpenedId: undefined }
  return { lastOpenedId: prefs?.last_opened_id ?? null }
}

export function _buildSetterPayload(
  prefs: DashboardPreferences | undefined,
  current: string | null | undefined,
  next: string | null
): { section: "dashboard_preferences"; data: Record<string, unknown> } | null {
  if (next === current) return null
  const merged: DashboardPreferences = {
    ...(prefs ?? {}),
    last_opened_id: next,
  }
  return {
    section: "dashboard_preferences",
    data: merged as unknown as Record<string, unknown>,
  }
}

export function useDashboardLastOpened(): DashboardLastOpened {
  const { data, isLoading } = useUserSettings()
  const updateSection = useUpdateSettingsSection()

  const prefs: DashboardPreferences | undefined = data?.data.dashboard_preferences
  const { lastOpenedId } = _computeLastOpened(prefs, isLoading)

  const setLastOpenedId = useCallback(
    (id: string | null) => {
      const payload = _buildSetterPayload(prefs, lastOpenedId, id)
      if (!payload) return
      updateSection.mutate(payload, {
        // Without this onError the persist still fails — useUpdateSettingsSection
        // surfaces a generic toast — but a navigator never sees *which* persist
        // dropped (last-opened vs favourites vs anywhere else). The dedicated
        // log makes the trail attributable when scrolling the console after a
        // route flake.
        onError: (err) => {
          console.warn("[dash] failed to persist last-opened dashboard id", err)
        },
      })
    },
    [lastOpenedId, prefs, updateSection]
  )

  return { lastOpenedId, isLoading, setLastOpenedId }
}
